import { create } from "zustand";
import { dbGet, dbPut } from "../storage/db.js";
import { useFavoritesStore } from "./favoritesStore.js";
import { useSyncSettings } from "./syncSettingsStore.js";

/**
 * Per-channel stream health (technical-spec §8.2 / §8.3), persisted on-device.
 *
 * Owner, 2026-10-21: a channel that ends in an error after the player's whole retry protocol, on
 * THREE separate tries, is hidden but kept, and tested again at every playlist update. "Don't
 * lose channels that actually work":
 *
 *   - a failed try (library/channelFailures.ts decides what counts) → degraded; tries closer
 *     together than TRY_GAP_MS are one try, so pressing a channel three times hides nothing
 *   - FAILED_TRIES_TO_HIDE failed tries in a row → dead, hidden as a SYSTEM hide
 *   - one good play, or a re-test that answers → good, the count back to zero, and un-hidden
 *   - a re-test that fails changes nothing: a check alone never hides a channel
 *
 * Never hidden: a favorite (PPV/event channels the user stars can be dark between events), a
 * channel the user hid already (user hide wins, and recovery never un-hides it), anything while
 * Settings' "Hide channels that keep failing" is off, and anything past MAX_HIDES_PER_DAY — that
 * many dead channels at once is the provider failing, not the channels.
 *
 * System hides go through favoritesStore's hidden records with hiddenBy: "system", so the guide's
 * filtering hides them and Live TV's "Hidden" group still lists them.
 */
export type ChannelHealthState = "unknown" | "good" | "degraded" | "dead";

export interface ChannelHealth {
  state: ChannelHealthState;
  /** Failed tries in a row so far (0 after any success). */
  failStreak: number;
  lastCheckedAt: string | null;
  /** HTTP status of the last failure or check, or null when it never responded. */
  lastStatus: number | null;
  /** When the last counted failed try happened (epoch ms). */
  lastFailedAt?: number;
}

/** Raw result shape from POST /api/health/probe, and from the TV's own check. */
export interface ProbeResult {
  url: string;
  ok: boolean;
  status: number | null;
  checkedAt: string;
}

export interface ChannelProbeResult {
  channelId: string;
  result: ProbeResult;
}

/** Owner, 2026-10-21: "three tries make it really safe". */
export const FAILED_TRIES_TO_HIDE = 3;
/** Failures of one channel closer together than this are the same try. */
export const TRY_GAP_MS = 10 * 60 * 1000;
/** More hides than this in a day means the provider is failing: stop hiding. */
export const MAX_HIDES_PER_DAY = 20;
const DAY_MS = 24 * 60 * 60 * 1000;

const UNKNOWN: ChannelHealth = { state: "unknown", failStreak: 0, lastCheckedAt: null, lastStatus: null };
const HEALTH_KEY = "health";
const HIDES_KEY = "hides";

/** How many recently probed channel ids the guide remembers, to rotate its sweep. */
const RECENT_PROBE_KEEP = 200;

/**
 * What one probe cycle actually managed to do (server, 2026-09-24).
 *
 * The account allows a handful of provider sessions a minute and the guide's health sweep gets a
 * smaller share of them, so a whole screenful of channels is not paid for in one cycle. The
 * server answers with the count it could not pay for and when it can pay again; the guide uses
 * that to pace itself (syncActions) instead of re-asking the same batch every refresh.
 */
export interface ProbeOutcome {
  /** Ids whose manifest the server really probed this cycle. */
  probedIds: string[];
  /** Channels the allowance could not pay for — skipped, never reported as failures. */
  skipped: number;
  /** Seconds until the allowance could pay for another probe (0 = ask whenever). */
  retryAfterSeconds: number;
  /** Injectable clock for tests. */
  now?: number;
}

interface ChannelHealthStoreState {
  /** channelId → health record. Only channels that ever failed or were checked appear. */
  health: Record<string, ChannelHealth>;
  /** When each system hide of the last day happened (epoch ms): the daily cap. */
  recentHides: number[];
  hydrated: boolean;
  /** Ask-freely marker: the guide stays quiet while `Date.now() < nextProbeAt`. */
  nextProbeAt: number | null;
  /** Channel ids the recent cycles reached, most recent LAST — the sweep's rotation order. */
  recentProbeIds: string[];
  hydrate: () => Promise<void>;
  healthOf: (channelId: string) => ChannelHealth;
  /** A try that ended in the player's error, after every retry: counts toward the hide. */
  recordFailedTry: (channelId: string, failure: { at: number; status: number | null }) => void;
  /** The channel played: whatever it failed before, it works. */
  recordPlayed: (channelId: string) => void;
  /** Re-test results: an answer recovers the channel; no answer changes nothing. */
  recordProbeResults: (results: ChannelProbeResult[]) => void;
  /** "Show anyway": back in the guide with a clean count. */
  showAnyway: (channelId: string) => void;
  /** The switch went off: every system hide comes back. */
  releaseSystemHides: () => void;
  /** Remember what the cycle cost, so the next one starts where this one ran out. */
  noteProbeOutcome: (outcome: ProbeOutcome) => void;
}

function persist(health: Record<string, ChannelHealth>, recentHides: number[]): void {
  void dbPut("channelHealth", health, HEALTH_KEY).catch(() => undefined);
  void dbPut("channelHealth", recentHides, HIDES_KEY).catch(() => undefined);
}

export const useChannelHealthStore = create<ChannelHealthStoreState>()((set, get) => ({
  health: {},
  recentHides: [],
  hydrated: false,
  nextProbeAt: null,
  recentProbeIds: [],

  hydrate: async () => {
    if (get().hydrated) return;
    // Read by key: the rows are stored bare (dbPut's out-of-line key), so a getAll never carried
    // the key, and until 2026-10-21 nothing persisted was ever read back.
    const [health, hides] = await Promise.all([
      dbGet<Record<string, ChannelHealth>>("channelHealth", HEALTH_KEY).catch(() => undefined),
      dbGet<number[]>("channelHealth", HIDES_KEY).catch(() => undefined),
    ]);
    set({
      health: health && typeof health === "object" ? health : {},
      recentHides: Array.isArray(hides) ? hides : [],
      hydrated: true,
    });
  },

  healthOf: (channelId) => get().health[channelId] ?? UNKNOWN,

  recordFailedTry: (channelId, { at, status }) => {
    const prev = get().health[channelId] ?? UNKNOWN;
    if (prev.lastFailedAt !== undefined && at - prev.lastFailedAt < TRY_GAP_MS) return;
    const failStreak = prev.failStreak + 1;
    const state: ChannelHealthState = failStreak >= FAILED_TRIES_TO_HIDE ? "dead" : "degraded";
    let recentHides = get().recentHides.filter((t) => at - t < DAY_MS);
    const favorites = useFavoritesStore.getState();
    if (
      state === "dead" &&
      useSyncSettings.getState().hideFailingChannels &&
      recentHides.length < MAX_HIDES_PER_DAY &&
      !favorites.isFavorite("channel", channelId) &&
      !favorites.isHidden("channel", channelId)
    ) {
      favorites.systemHideChannel(channelId);
      recentHides = [...recentHides, at];
    }
    const health = {
      ...get().health,
      [channelId]: { state, failStreak, lastCheckedAt: new Date(at).toISOString(), lastStatus: status, lastFailedAt: at },
    };
    set({ health, recentHides });
    persist(health, recentHides);
  },

  recordPlayed: (channelId) => {
    const prev = get().health[channelId];
    // Only channels with a record: every good play would otherwise store a row per channel.
    if (!prev || (prev.state === "good" && prev.failStreak === 0)) return;
    useFavoritesStore.getState().systemUnhideChannel(channelId);
    const health = {
      ...get().health,
      [channelId]: { state: "good" as const, failStreak: 0, lastCheckedAt: new Date().toISOString(), lastStatus: null },
    };
    set({ health });
    persist(health, get().recentHides);
  },

  recordProbeResults: (results) => {
    if (results.length === 0) return;
    const next = { ...get().health };
    const favorites = useFavoritesStore.getState();
    for (const { channelId, result } of results) {
      const prev = next[channelId] ?? UNKNOWN;
      if (result.ok) {
        // Only a system hide comes back; a user hide is untouched by design.
        favorites.systemUnhideChannel(channelId);
        next[channelId] = { state: "good", failStreak: 0, lastCheckedAt: result.checkedAt, lastStatus: result.status };
      } else {
        next[channelId] = { ...prev, lastCheckedAt: result.checkedAt, lastStatus: result.status };
      }
    }
    set({ health: next });
    persist(next, get().recentHides);
  },

  showAnyway: (channelId) => {
    useFavoritesStore.getState().systemUnhideChannel(channelId);
    const health = { ...get().health };
    delete health[channelId];
    set({ health });
    persist(health, get().recentHides);
  },

  releaseSystemHides: () => {
    const favorites = useFavoritesStore.getState();
    for (const row of Object.values(favorites.hidden)) {
      if (row.contentType === "hidden-channel" && row.hiddenBy === "system") favorites.systemUnhideChannel(row.contentRef);
    }
  },

  noteProbeOutcome: ({ probedIds, skipped, retryAfterSeconds, now = Date.now() }) => {
    // Each id once, most recent last: the next cycle checks the channels this one could not reach
    // before it re-checks any of these (the server can only pay for a few at a time).
    const recent = [...get().recentProbeIds.filter((id) => !probedIds.includes(id)), ...probedIds].slice(
      -RECENT_PROBE_KEEP,
    );
    set({
      recentProbeIds: recent,
      nextProbeAt: skipped > 0 && retryAfterSeconds > 0 ? now + retryAfterSeconds * 1000 : null,
    });
  },
}));

/** Hydrate once at module load so guide badges never flash "unknown". */
void useChannelHealthStore.getState().hydrate();
